//
// One field takes a company name or a SIRET: fourteen digits are searched as a
// SIRET, anything else as a name, optionally narrowed to a postcode prefix. The
// caller owns the request (an inviter's session, or an invitation link), so the
// same search serves both sides of a partnership.
//
import PropTypes from 'prop-types';
import {
  useCallback,
  useState,
} from 'react';

import { useTownAssets } from '../../stores';
import {
  logError,
  regex,
} from '../../utils';
import { Button } from '../buttons';
import {
  Column,
  Row,
} from '../display';
import {
  RadioButton,
  TextInput,
} from '../input';

import CompanyGovSummary from './CompanyGovSummary';

const searchReqOf = (text, zipPrefix) => {
  const compact = text.replaceAll(' ', '');
  if (/^[0-9]{14}$/.test(compact)) return { searchSiret: compact };

  const searchName = text.trim();
  if (!regex.companySearchName.test(searchName)) return null;

  const searchZipPrefix = zipPrefix.trim();
  if (searchZipPrefix && (!regex.postCodePrefix.test(searchZipPrefix))) return null;

  return {
    searchName,
    ...((searchZipPrefix) ? { searchZipPrefix } : {}),
  };
};

// The server's answer rows, as the sentence shown for each outcome
const outcomeKeyOf = (rows) => {
  if (rows?.[0]?.tooManyResults) return 'companySearchTooManyResults';
  if (rows?.[0]?.searchLimitReached) return 'companySearchLimitReached';
  if (rows?.[0]?.invalidStatus) return 'inviteStatusError';
  if (!rows?.length) return 'companySearchNoResult';

  return undefined;
};

function CompanyGovSearch(props) {
  const {
    search,
    picked,
    onPick,
    disabled,
  } = props;
  const { assets } = useTownAssets();
  const { translate } = assets;

  const [text, setText] = useState('');
  const [zipPrefix, setZipPrefix] = useState('');
  const [pending, setPending] = useState(false);
  const [results, setResults] = useState([]);
  const [outcomeKey, setOutcomeKey] = useState(undefined);

  const searchReq = searchReqOf(text, zipPrefix);

  const runSearch = useCallback(
    async () => {
      if (!searchReq) return;

      setPending(true);
      setOutcomeKey(undefined);

      let rows;
      try {
        rows = await search({
          cstmrCtry: 'fr',
          ...searchReq,
        });
      } catch (err) {
        logError(
          `CompanyGovSearch ${err?.message ?? err}`,
          'CompanyGovSearch.search.error',
          {},
        );
        setResults([]);
        setOutcomeKey('govIdSearchFailed');
        setPending(false);
        return;
      }

      const outcome = outcomeKeyOf(rows);
      setResults((outcome) ? [] : rows);
      setOutcomeKey(outcome);
      setPending(false);

      // A single match is the one meant
      if ((rows?.length === 1) && (!outcome)) onPick(rows[0]);
    },
    [search, searchReq, onPick],
  );

  const onKeyDown = useCallback(
    (event) => {
      // Use void to make it clear we do not want to wait this async function:
      if (event.key === 'Enter') void runSearch();
    },
    [runSearch],
  );

  return (
    <Column crossStart gap={4} className="w-full">
      <Row gap={2}>
        <TextInput
          id="companySearchText"
          name="companySearchText"
          value={text}
          onChange={(event) => setText(event.currentTarget.value)}
          onKeyDown={onKeyDown}
          placeholder={translate('companySearchNameOrSiret', 'statements')}
          widthChars={28}
          invalid={Boolean(text) && (!searchReq)}
          disabled={disabled}
          focus
        />

        <TextInput
          id="companySearchZip"
          name="companySearchZip"
          value={zipPrefix}
          onChange={(event) => setZipPrefix(event.currentTarget.value)}
          onKeyDown={onKeyDown}
          placeholder={translate('postCode')}
          widthChars={10}
          invalid={Boolean(zipPrefix) && (!regex.postCodePrefix.test(zipPrefix.trim()))}
          disabled={disabled || Boolean(searchReq?.searchSiret)}
          optional
        />

        <Button
          id="companySearch"
          imgSrc="/images/svg/gui/magnifier.svg"
          onClick={runSearch}
          disabled={disabled || (!searchReq)}
          pending={pending}
          baseColor
        />
      </Row>

      {(outcomeKey)
        ? <span className="text-pxUiAlert italic">{translate(outcomeKey, 'statements')}</span>
        : null}

      {(results.length > 1)
        ? (
            <Column crossStart gap={2} className="w-full max-h-48 overflow-y-auto">
              {results.map((row) => (
                <RadioButton
                  key={row.cstmrGovId}
                  id={row.cstmrGovId}
                  name="companySearchResult"
                  label={[
                    row.cstmrGovLbl,
                    row.cstmrSignLbl,
                    row.cstmrRegisteredAddress?.govAddressZipCode,
                    row.cstmrRegisteredAddress?.govAddressCity,
                  ].filter(Boolean).join(' - ')}
                  onChange={() => onPick(row)}
                  selected={picked?.cstmrGovId === row.cstmrGovId}
                  disabled={disabled}
                />
              ))}
            </Column>
          )
        : null}

      {(picked) ? <CompanyGovSummary company={picked} /> : null}
    </Column>
  );
}

CompanyGovSearch.propTypes = {
  // Resolves the server's answer rows for one search request
  search: PropTypes.func.isRequired,
  picked: PropTypes.shape({
    cstmrGovId: PropTypes.string,
  }),
  onPick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default CompanyGovSearch;
